/**
 * Auto-classification suggestion data hooks (task 204).
 *
 * Two hooks today:
 *
 *  - `useSuggestion(eventId)` — fetches the latest suggestion row for an
 *    event via the backend lane's `GET
 *    /api/v1/events/:eventId/suggestion`. Mockup mode returns a
 *    deterministic fixture.
 *  - `usePromoteSuggestion()` — mutation wrapper around `POST
 *    /api/v1/events/:eventId/suggestion/promote`. Mockup mode short-
 *    circuits with a synthetic response shape (mirrors
 *    `useClassifyWorkLog`'s mockup-no-op pattern).
 *
 * The hooks live in `src/hooks/` so the WhyPanel can stay a presentational
 * composite (no fetching inside the composite — same shape as the rest of
 * the codebase).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import type { PromoteRequest, PromoteResponse, SuggestionResponse } from '@/types/suggestion';

interface UseSuggestionResult {
  data: SuggestionResponse | null;
  isLoading: boolean;
}

// Deterministic mockup fixture. Keeps the WhyPanel renderable in fixture
// mode without bringing the full event_classification_suggestion shape
// into src/data/. The values are tuned so the worth-a-glance state
// renders (0.78 < AI_AUTO_PROMOTE_CONFIDENCE) — pages that need a
// settled or unclassified fixture can override via the hook's seed
// argument once a caller needs it.
function mockupFixture(eventId: string, isAdmin: boolean): SuggestionResponse {
  return {
    id: `sugg-${eventId}`,
    eventId,
    pass: 'vision',
    status: 'pending',
    fused: 0.78,
    signals: {
      perSignal: {
        rule: { score: 0, reason: 'No matching rule.' },
        text: {
          score: 0.72,
          reason: 'Title mentions “Meridian M&A diligence”.',
        },
        vision: {
          score: 0.82,
          reason: 'Screenshot shows a familiar deal-room layout.',
        },
        neighbour: {
          score: 0.61,
          reason: 'Adjacent events were classified to the same project.',
        },
      },
      weightsVersion: 'v0.1',
    },
    topProject: { id: 'prj-meridian-mna', name: 'M&A Diligence' },
    reasoning:
      'Based on the title and the visible deal-room layout in the screenshot, this looks like Meridian M&A Diligence work — same pattern as the events on either side of it.',
    screenshotId: 'shot-1',
    aiCallLogId: isAdmin ? 'aicall-mockup-1' : null,
    modelVersion: isAdmin ? 'claude-sonnet-4-vision' : null,
    overrideCount: 0,
  };
}

export function useSuggestion(eventId: string | null): UseSuggestionResult {
  const { mockupMode, currentUser } = useAppContext();
  const getToken = useToken();

  const query = useQuery({
    queryKey: ['suggestion', eventId],
    queryFn: async () => {
      if (!eventId) return null;
      const token = await getToken();
      return apiFetch<SuggestionResponse>(`/api/v1/events/${eventId}/suggestion`, token);
    },
    enabled: !mockupMode && !!eventId,
  });

  if (mockupMode) {
    return {
      data: eventId ? mockupFixture(eventId, currentUser.role === 'admin') : null,
      isLoading: false,
    };
  }
  return { data: query.data ?? null, isLoading: query.isLoading };
}

interface PromoteVariables extends PromoteRequest {
  eventId: string;
}

export function usePromoteSuggestion() {
  const { mockupMode } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation<PromoteResponse, Error, PromoteVariables>({
    mutationFn: async (vars) => {
      if (mockupMode) {
        // Fixture-mode synthetic response. The page surfaces success
        // verbiage; fixture state itself is unchanged.
        const ruleCandidate =
          vars.action === 'dismiss'
            ? null
            : {
                name: 'Auto-suggest: Meridian deal-room',
                priority: 100,
                condition: {
                  type: 'pattern' as const,
                  field: 'title' as const,
                  value: 'Meridian M&A',
                },
                projectId: vars.projectId ?? 'prj-meridian-mna',
                description:
                  'Window titles that include “Meridian M&A” match the M&A Diligence project.',
              };
        return {
          classifiedEventId: vars.action === 'dismiss' ? null : `ce-${vars.eventId}`,
          ruleCandidate,
        };
      }
      const token = await getToken();
      const body: PromoteRequest = {
        action: vars.action,
        projectId: vars.projectId,
        suggestionId: vars.suggestionId,
      };
      return apiFetch<PromoteResponse>(`/api/v1/events/${vars.eventId}/suggestion/promote`, token, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['suggestion', vars.eventId] });
      // The promote endpoint also writes classified_event — refresh the
      // surfaces that show classified state.
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'workLogReview' });
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'appTitleLog' });
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'appTitleLogEvents' });
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'needsReviewCount' });
    },
  });
}
